import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Grid, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Delete } from '@mui/icons-material';

import Breadcrumb from 'components/ui/Breadcrumb';
import GalleryAddImageForm from '../components/gallery/GalleryAddImageForm';
import { toast } from 'react-toastify';
import { deleteGalleryImageRequest, getGalleryImageRequest, saveGalleryImageRequest } from '../services/user';
import { useSelector } from 'react-redux';
import { selectUserYearBookGalleryLimit, selectUserYearBookIncludeGallery } from '../store/slices/authSlice';

const useStyles = makeStyles((theme) => ({
    mainHeading: {
        margin: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
            textAlign: 'center'
        }
    },
    breadcrumbs: {
        display: 'flex',
        [theme.breakpoints.down('md')]: {
            justifyContent: 'center'
        }
    }
}));

function GalleryPage(props) {
    const [galleryImages, setGalleryImages] = useState([]);
    const [galleryImagesLoading, setGalleryImagesLoading] = useState(true);
    const [galleryImagesMutation, setGalleryImagesMutation] = useState({});

    const galleryLimit = useSelector(selectUserYearBookGalleryLimit);
    const galleryAllowed = useSelector(selectUserYearBookIncludeGallery);

    const classes = useStyles();

    const navigate = useNavigate();

    const handleAddImageSubmit = async (values, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        if (!values?.image?.key) {
            toast.error('Please select an image to upload');
        } else {
            const result = await saveGalleryImageRequest({ image: values?.image?.key });
            if (result.error === false) {
                setGalleryImagesMutation({});
                resetForm();
            }
        }
        setSubmitting(false);
    };

    const handleImageDelete = async (imageId) => {
        const result = await deleteGalleryImageRequest(imageId);
        if (result.error === false) {
            setGalleryImagesMutation({});
        }
    };

    useEffect(() => {
        async function fetchGalleryImages() {
            setGalleryImagesLoading(true);
            const response = await getGalleryImageRequest();
            if (response.error === false) {
                let images = response?.results ?? [];
                const baseUrl = response?.imageBaseUrl ?? '';
                images = images.map((imageObj) => ({
                    ...imageObj,
                    image: `${baseUrl}/${imageObj.image}`
                }));
                setGalleryImages(images);
            }
            setGalleryImagesLoading(false);
        }

        fetchGalleryImages();
    }, [galleryImagesMutation]);

    return (
        <>
            <Breadcrumb title="Gallery" />
            <Grid container justifyContent="center">
                <Grid item xs={12} lg={11} xl={9}>
                    {galleryAllowed ? (
                        <Grid container spacing={2}>
                            {galleryImages.length < galleryLimit && (
                                <Grid item xs={12}>
                                    <Card>
                                        <CardContent>
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                                        <Typography variant="h4">Add new image</Typography>
                                                        <Box sx={{ ml: 0.5 }}>
                                                            <small>
                                                                (Uploaded {galleryImages.length} out of {galleryLimit})
                                                            </small>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <GalleryAddImageForm handleSubmit={handleAddImageSubmit} />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                                    <Typography variant="h4">Gallery Images</Typography>
                                                    <Box sx={{ ml: 0.5 }}>
                                                        <small>
                                                            (Uploaded {galleryImages.length} out of {galleryLimit})
                                                        </small>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                {galleryImagesLoading ? (
                                                    <Typography>Loading...</Typography>
                                                ) : (
                                                    <Grid container spacing={2}>
                                                        {galleryImages.map((imageObj) => (
                                                            <Grid item xs={12} md={4}>
                                                                <Box
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '15rem',
                                                                        position: 'relative'
                                                                    }}
                                                                >
                                                                    <img
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            objectFit: 'cover'
                                                                        }}
                                                                        src={imageObj?.image}
                                                                        alt=""
                                                                    />
                                                                    <IconButton
                                                                        onClick={() => handleImageDelete(imageObj._id)}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: 5,
                                                                            right: 5
                                                                        }}
                                                                    >
                                                                        <Delete color="error" />
                                                                    </IconButton>
                                                                </Box>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Grid container>
                                            <Grid item xs={12}>
                                                <Typography variant="h4">Gallery not available for your yearbook</Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </>
    );
}

export default GalleryPage;
